import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import type {
  BidComparison,
  BidComparisonScope,
  BidComparisonSheet,
  BidComparisonTemplate,
} from '@/features/bid-comparison/types';
import { isComparisonProcessing } from '@/features/bid-comparison/utils';
import { REFETCH_SUMMARIZATION_INTERVAL } from '@/features/workspaces/constants';

export const bidComparisonQuery = (
  workspaceId: string,
  sheetId?: string,
  scope?: BidComparisonScope,
) =>
  queryOptions({
    queryKey: [
      'bidComparison',
      workspaceId,
      ...(scope ? [scope] : []),
      ...(sheetId ? [sheetId] : []),
    ],
    queryFn: async () =>
      (
        await api.get<BidComparison>('v1/analysis/bid_comparison', {
          params: {
            type: DOCUMENT_TYPE.tenderPackage,
            resourceId: workspaceId,
            sheetId: sheetId || undefined,
            ...scope,
          },
        })
      ).data,
    retryOnMount: false,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isComparisonProcessing(query.state.data)
        ? REFETCH_SUMMARIZATION_INTERVAL * 1000
        : false;
    },
  });

export const bidComparisonSheetsQuery = (workspaceId: string, scope?: BidComparisonScope) =>
  queryOptions({
    queryKey: ['bidComparisonSheets', workspaceId, ...(scope ? [scope] : [])],
    queryFn: async () =>
      (
        await api.get<{ sheets: BidComparisonSheet[] }>('v1/analysis/bid_comparison/sheets', {
          params: { type: DOCUMENT_TYPE.tenderPackage, resourceId: workspaceId, ...scope },
        })
      ).data.sheets,
  });

export const bidComparisonTemplatesQuery = (workspaceId: string, scope?: BidComparisonScope) =>
  queryOptions({
    queryKey: ['bidComparisonTemplates', workspaceId, ...(scope ? [scope] : [])],
    queryFn: async () =>
      (
        await api.get<{ templates: BidComparisonTemplate[] }>(
          'v1/analysis/bid_comparison/templates',
          {
            params: {
              type: DOCUMENT_TYPE.tenderPackage,
              resourceId: workspaceId,
              offerType: DOCUMENT_TYPE.bid,
              ...scope,
            },
          },
        )
      ).data.templates,
  });
